/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, { useCallback } from 'react';
import { View, ViewStyle } from 'react-native';
import SkeletonLoaderView from '@airasia-phoenix/react-native/components/SkeletonLoaderView';
import { getShape, pd } from '@airasia-phoenix/react-native/utils';
import type { SkeletonLoaderWrapperProps } from './types';

const DEFAULTS = {
  GAP: pd.ref.space.spacing10.px,
  BORDER_RADIUS_SHAPE: pd.sys.shape.corner.medium,
} as const;

const SkeletonLoaderWrapper = ({
  height,
  itemWidth,
  itemCount = 1,
  containerStyle,
  orientation = 'horizontal',
  testID = 'skeleton-loader-container',
  borderRadiusShape = DEFAULTS.BORDER_RADIUS_SHAPE,
}: SkeletonLoaderWrapperProps) => {
  const isHorizontal = orientation === 'horizontal';
  const skeletonArray = Array.from({ length: itemCount });

  const getItemStyle = useCallback(
    (index: number): ViewStyle => {
      const isLastItem = index === itemCount - 1;

      return {
        height,
        width: itemWidth,
        marginRight: isHorizontal && !isLastItem ? DEFAULTS.GAP : 0,
        marginBottom: !isHorizontal && !isLastItem ? DEFAULTS.GAP : 0,
        ...getShape(borderRadiusShape),
      };
    },
    [height, itemWidth, isHorizontal, itemCount, borderRadiusShape],
  );

  return (
    <View
      testID={testID}
      style={[
        {
          flexDirection: isHorizontal ? 'row' : 'column',
        },
        containerStyle,
      ]}
    >
      {skeletonArray.map((_, index) => (
        <SkeletonLoaderView
          key={`skeleton-${index}`}
          style={getItemStyle(index)}
        />
      ))}
    </View>
  );
};

export default SkeletonLoaderWrapper;
